import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import type { TeleStaffChatMessage, TeleconsultManagementVisit } from '../../types/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { parseChatMessages } from '../staff-teleconsult/staffChatMessages';
import { formatManagementClock, formatManagementDay } from './formatManagementTime';

type Props = {
  visit: TeleconsultManagementVisit;
  onClose: () => void;
};

type Incharge = {
  name: string | null;
  id: number | null;
  code: string | null;
};

function formatChatTs(ts: number): string {
  if (!Number.isFinite(ts)) return '';
  return formatManagementClock(new Date(ts).toISOString()) || new Date(ts).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Staff lines prefer the name stored on the message (GET staff-chat also enriches historical JSON).
 * Fallbacks: incharge / closed-by display name from API, then visit registration staff, else "Staff".
 * Partner stays "Partner" (corporate partner chat); staff side still uses the name chain above.
 */
export function chatSenderLabel(
  message: TeleStaffChatMessage,
  visit: TeleconsultManagementVisit,
  incharge: Incharge,
): string {
  if (message.sender === 'patient') return (visit.patientName || '').trim() || 'Patient';
  if (message.sender === 'partner') return 'Partner';
  if (message.sender === 'system') return 'System';

  const named = (message.staffName || '').trim();
  if (named) return named;

  if (incharge.name) return incharge.name;
  if (incharge.code) return incharge.code;

  const regStaff = (visit.registrationStaffName || '').trim();
  if (regStaff && regStaff.toLowerCase() !== 'system') return regStaff;

  if (message.staffEmpId != null && message.staffEmpId > 0) return `Staff ${message.staffEmpId}`;
  if (incharge.id != null && incharge.id > 0) return `Staff ${incharge.id}`;
  return 'Staff';
}

/**
 * Read-only historical chat for Patient Log.
 * Messages are real DOM text so browser Ctrl+F / Cmd+F works inside the dialog.
 */
export default function ChatTranscriptDialog({ visit, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState<TeleStaffChatMessage[]>([]);
  const [incharge, setIncharge] = useState<Incharge>({ name: null, id: null, code: null });
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(() => {
    setReloadKey((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void (async () => {
      try {
        const res = await apiService.getTeleconsultStaffChat(visit.visitUlid);
        if (cancelled) return;
        setMessages(parseChatMessages(res.chatConversation));
        setIncharge({
          name: (res.inchargeDisplayName || '').trim() || null,
          id: res.inchargeEmpId ?? null,
          code: (res.inchargeEmpCode || '').trim() || null,
        });
      } catch (err: unknown) {
        if (cancelled) return;
        setError(getApiErrorMessage(err, 'Could not load chat.'));
        setMessages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visit.visitUlid, reloadKey]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const titleId = `mgmt-chat-title-${visit.visitUlid}`;
  const usedInchargeFallback = messages.some(
    (m) =>
      m.sender === 'staff'
      && !(m.staffName || '').trim()
      && Boolean(
        incharge.name
        || incharge.code
        || incharge.id
        || ((visit.registrationStaffName || '').trim()
          && (visit.registrationStaffName || '').trim().toLowerCase() !== 'system'),
      ),
  );

  return (
    <div className="staff-mgmt__dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="staff-mgmt__dialog staff-mgmt__chat-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="staff-mgmt__chat-dialog-head">
          <div>
            <h2 className="staff-mgmt__dialog-title" id={titleId}>
              Conversation — {visit.patientName || 'Patient'}
            </h2>
            <p className="staff-mgmt__dialog-lead">
              {formatManagementDay(visit.registeredAt || visit.visitDate)}
              {' · '}
              <code className="staff-mgmt__chat-ulid">{visit.visitUlid}</code>
            </p>
          </div>
          <button type="button" className="mdash-modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="staff-mgmt__chat-transcript" tabIndex={0} aria-busy={loading}>
          {loading ? (
            <p className="staff-mgmt__chat-status" role="status">
              Loading chat…
            </p>
          ) : null}
          {!loading && error ? (
            <div className="staff-mgmt__chat-status" role="alert">
              <p className="staff-mgmt__dialog-error">{error}</p>
              <button type="button" className="mdash-linkish" onClick={load}>
                Try again
              </button>
            </div>
          ) : null}
          {!loading && !error && messages.length === 0 ? (
            <p className="staff-mgmt__chat-status" role="status">
              No messages in this conversation.
            </p>
          ) : null}
          {!loading && !error
            ? messages.map((m, i) => (
                <div
                  key={`${m.ts}-${m.sender}-${i}`}
                  className={`staff-mgmt__chat-line staff-mgmt__chat-line--${m.sender}`}
                >
                  <span className="staff-mgmt__chat-meta">
                    {chatSenderLabel(m, visit, incharge)}
                    {'  '}
                    {formatChatTs(m.ts)}
                  </span>
                  <span className="staff-mgmt__chat-text">
                    {m.text?.trim()
                      ? m.text
                      : m.imageUlid
                        ? '[image]'
                        : ''}
                  </span>
                </div>
              ))
            : null}
        </div>

        <p className="staff-mgmt__chat-tip">Tip: Ctrl+F / Cmd+F to find text in this window</p>
        {usedInchargeFallback ? (
          <p className="staff-mgmt__chat-tip">
            Some older staff lines had no per-message author; labels use the chat handler
            {incharge.name
              ? ` (${incharge.name})`
              : (visit.registrationStaffName || '').trim()
                ? ` (${(visit.registrationStaffName || '').trim()})`
                : ''}
            .
          </p>
        ) : null}
        <div className="staff-mgmt__dialog-buttons">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
